import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Save, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { usePageHeader } from '../components/Layout';
import { SectionCard } from '../design/primitives';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function RoleManagement() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  // Workspace ekranında görünmesi gereken sıra:
  // admin > Proje Yöneticisi > Takım Lideri > Takım Üyesi. Süper admin
  // sistem rolü olduğundan workspace yöneticisinin görmesine gerek yok.
  const ROLE_DISPLAY_ORDER = useMemo(() => ({
    admin: 0,
    proje_yoneticisi: 1,
    takim_lideri: 2,
    takim_uyesi: 3,
    yorumcu: 4,
  }), []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        axios.get(`${API}/roles`),
        axios.get(`${API}/roles/available-permissions`),
      ]);
      const filtered = (rolesRes.data || []).filter(r => r.name !== 'super_admin');
      filtered.sort((a, b) => {
        const ai = ROLE_DISPLAY_ORDER[a.name];
        const bi = ROLE_DISPLAY_ORDER[b.name];
        if (ai === undefined && bi === undefined) return (a.label || '').localeCompare(b.label || '', 'tr');
        if (ai === undefined) return 1;
        if (bi === undefined) return -1;
        return ai - bi;
      });
      setRoles(filtered);
      setAvailablePermissions(permsRes.data);
    } catch {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (roleId, permissionId) => {
    setRoles(prev => prev.map(r => {
      if (r.id !== roleId) return r;
      const has = r.permissions.includes(permissionId);
      return {
        ...r,
        permissions: has ? r.permissions.filter(p => p !== permissionId) : [...r.permissions, permissionId],
      };
    }));
  };

  const hasPermission = (role, pid) => role.permissions.includes(pid);

  const saveChanges = async () => {
    setSaving(true);
    try {
      // Backend artık aktif workspace için PATCH /api/roles/{name} ile rolün
      // izinlerini override ediyor; admin (her zaman tam yetki) düzenlenmez.
      const toUpdate = roles.filter(r => r.editable !== false && r.name !== 'admin');
      await Promise.all(
        toUpdate.map(r => axios.patch(`${API}/roles/${r.name}`, { permissions: r.permissions }))
      );
      toast.success('Değişiklikler kaydedildi');
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Değişiklikler kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const headerActions = useMemo(() => (
    <>
      <button className="ox-btn ox-btn-ghost" onClick={() => navigate('/settings/members')}>
        <ArrowLeft size={13} style={{ marginRight: 6 }} />
        Üyeler
      </button>
      <button className="ox-btn ox-btn-primary" onClick={saveChanges} disabled={saving}>
        <Save size={13} style={{ marginRight: 6 }} />
        {saving ? 'Kaydediliyor...' : 'Kaydet'}
      </button>
    </>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [saving, roles]);

  usePageHeader([{ label: 'Rol Yönetimi' }], headerActions, [saving, roles]);

  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="ox-surface rounded-xl p-8 max-w-md w-full text-center" style={{ animation: 'oxSlideUp .25s cubic-bezier(.2,.8,.2,1)' }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--danger-weak)' }}>
            <Shield size={32} style={{ color: 'var(--danger)' }} />
          </div>
          <h2 className="ox-h2 mb-2">Erişim Reddedildi</h2>
          <p className="ox-page-sub mb-6">
            Bu sayfaya erişim için <strong>admin</strong> yetkisi gereklidir.
          </p>
          <button
            onClick={() => window.history.length > 1 ? window.history.back() : window.location.assign('/')}
            className="ox-btn ox-btn-primary"
            style={{ minHeight: 40 }}
          >
            Geri Dön
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={28} className="animate-spin" style={{ color: 'var(--accent)' }} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="ox-surface rounded-md p-3 flex items-start gap-2" style={{ background: 'var(--accent-weak)', borderColor: 'var(--accent)' }}>
        <Shield size={14} style={{ color: 'var(--accent)' }} className="mt-0.5 flex-shrink-0" />
        <div className="text-xs">
          Checkbox'ları işaretleyerek rollere yetki atayabilirsiniz.{' '}
          <strong style={{ color: 'var(--accent)' }}>Admin rolü her zaman tüm yetkilere sahiptir ve düzenlenemez.</strong>{' '}
          Değişikliklerinizi kaydetmeyi unutmayın.
        </div>
      </div>

      <SectionCard title={`Yetki Matrisi (${roles.length} rol)`} pad={false}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider ox-text-dim" style={{ borderBottom: '1px solid var(--border)' }}>
                <th className="text-left px-3 py-2 font-medium sticky left-0 z-10" style={{ background: 'var(--surface)' }}>
                  Yetki
                </th>
                {roles.map(role => (
                  <th key={role.id} className="px-3 py-2 text-center min-w-[120px]">
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="text-[12px] font-semibold" style={{ color: 'var(--text)' }}>{role.label}</span>
                      <span className="text-[10px] ox-mono ox-text-dim">{role.name}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {availablePermissions.map((permission) => (
                <tr key={permission.id} className="ox-row-hover" style={{ borderBottom: '1px solid var(--border)' }}>
                  <td className="px-3 py-2 sticky left-0 z-10" style={{ background: 'var(--surface)' }}>
                    <div className="text-[13px] font-medium">{permission.label}</div>
                    <div className="text-[11px] ox-text-muted mt-0.5">{permission.description}</div>
                  </td>
                  {roles.map(role => {
                    const isAdmin = role.name === 'admin';
                    const checked = isAdmin || hasPermission(role, permission.id);
                    return (
                      <td key={`${role.id}-${permission.id}`} className="px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => !isAdmin && togglePermission(role.id, permission.id)}
                          disabled={isAdmin}
                          className="inline-flex items-center justify-center rounded"
                          style={{
                            width: 18,
                            height: 18,
                            border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                            background: checked ? 'var(--accent)' : 'transparent',
                            color: '#fff',
                            opacity: isAdmin ? 0.5 : 1,
                            cursor: isAdmin ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {checked && <Check size={12} strokeWidth={3} />}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

    </div>
  );
}
